import React from 'react';
import styled from 'styled-components';
import {FEATURE_FLAG_PROGRESS_FILL, FEATURE_FLAG_PROGRESS_TRACK} from './featureFlagSurface';

// Spans rather than divs so the bar is valid wherever it is dropped, including inside the flag row's
// inline cells, and blocks so it still fills its own line when it is not.
const Track = styled.span<{$height: string}>`
	display: block;
	/* Takes the space left over when a flex parent puts a percentage label beside the bar, and the
	   full width of its container when nothing sits next to it. */
	flex: 1;
	height: ${props => props.$height};
	border-radius: 999px;
	background: ${FEATURE_FLAG_PROGRESS_TRACK};
	overflow: hidden;
`;

const Fill = styled.span<{$percent: number}>`
	display: block;
	height: 100%;
	border-radius: 999px;
	background: ${FEATURE_FLAG_PROGRESS_FILL};
	width: ${props => props.$percent}%;
`;

export interface FeatureFlagRolloutBarProps {
	/** 0-100. Drives both the fill width and the value a screen reader reads off the gauge. */
	percent: number;
	/**
	 * The percentage as the page prints it beside the bar, e.g. `<1%`. Announced as the gauge's text
	 * value, so a screen reader hears what the page shows rather than the rounded number underneath it.
	 */
	label: string;
	/** Track height, e.g. `6px` — the one thing the flag list and a flag's detail page draw differently. */
	height: string;
}

/**
 * The rollout gauge, drawn once for both the flag list's rows and a flag's detail page.
 *
 * Shared so that the two screens cannot disagree about what a rollout looks like or about what it
 * announces: the role, the bounds, and the value all live here.
 */
export function FeatureFlagRolloutBar({percent, label, height}: FeatureFlagRolloutBarProps): JSX.Element {
	return (
		// The gauge is the track, not the fill inside it, so the progressbar role and its values sit
		// here and the fill is left as presentation.
		<Track
			aria-label="Rollout progress"
			role="progressbar"
			aria-valuenow={percent}
			aria-valuemin={0}
			aria-valuemax={100}
			aria-valuetext={label}
			$height={height}
		>
			<Fill data-testid="feature-flag-rollout-fill" $percent={percent} />
		</Track>
	);
}
